import type { GetStaticProps } from "next";
import { loadStandardContent } from "@/lib/content/site.server";
import Layout from "@/components/layout";
import SeoHead from "@/components/seo-head";
import ModelsConsultationForm from "@/components/forms/models-consultation";
import { MODELS_CONSULTATION_SOURCE } from "@/lib/models-consultation";
import Link from "next/link";
import {
  CalendarDaysIcon,
  PhoneIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

type Props = {
  siteData: any;
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  const siteData = await loadStandardContent();

  return {
    props: {
      siteData,
    },
    revalidate: 60,
  };
};

export default function ModelsConsultationPage({ siteData }: Props) {
  return (
    <main>
      <SeoHead
        meta={{
          title: "Book a Model Consultation | Cosmetic College",
          description:
            "Book a free model consultation. Share your details, then choose a time in our diary to discuss discounted beauty and aesthetics treatments.",
          canonicalPath: "/models/consultation/",
        }}
      />
      <Layout siteData={siteData}>
        <div className="overflow-hidden bg-white py-12">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-10 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:gap-y-16">
              <div className="lg:pr-8 lg:pt-4">
                <div className="lg:max-w-lg">
                  <p className="text-base font-semibold leading-7 text-brand-600">
                    Models
                  </p>
                  <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                    Book a free model consultation
                  </h1>
                  <p className="mt-4 text-base leading-7 text-gray-600 sm:text-lg">
                    Not sure which treatment is right for you? Tell us a little
                    about yourself, then pick a slot in our consultation diary.
                    Bookings land straight in the Models team calendar.
                  </p>

                  <ul className="mt-8 space-y-4 text-sm text-gray-700">
                    <li className="flex gap-3">
                      <SparklesIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                      <span>
                        Free, no-obligation consultation about discounted model
                        treatments
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <CalendarDaysIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                      <span>
                        Live diary availability - choose a time that suits you
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <PhoneIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                      <span>
                        Prefer a call? Request a callback and still book into
                        the same diary
                      </span>
                    </li>
                  </ul>

                  <div className="mt-8 text-sm">
                    <Link
                      href="/models/"
                      className="font-semibold text-brand-700 underline"
                    >
                      Browse model sessions
                    </Link>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-6 sm:p-8">
                <ModelsConsultationForm source={MODELS_CONSULTATION_SOURCE} />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </main>
  );
}
